/**
 * Maze generation and queries.
 *
 * Hybrid grid generator: streets form a regular city-block grid, then random
 * passages get punched through some buildings to create shortcuts and loops.
 * The result feels urban (predictable cross-streets, many alternate routes)
 * rather than mazelike (single twisty path with dead-ends).
 *
 * Layout, with blockWidth=4, blockHeight=3:
 *   #############################################
 *   #                                           #     <- street (y=1)
 *   # #### #### #### #### #### #### #### ####  #
 *   # #### #### #### #### #### #### #### ####  #     <- buildings
 *   # #### #### #### #### #### #### #### ####  #
 *   #                                           #     <- street (y=5)
 *   # #### ... etc
 *
 * Passages punch a single row or column of a building into corridor, joining
 * two parallel streets and creating loops.
 *
 * Flags are placed by shuffling all corridor tiles and greedily picking
 * positions that satisfy a Manhattan-distance constraint from already-placed
 * flags. Three of the placed flags are promoted to powerup kinds (doubler,
 * fuel_bonus, speed_boost); the rest are yellow.
 */

import { type Rng, createRng, nextFloat, nextInt, shuffle } from './rng.ts';

import type { FlagKind } from './types.ts';

export const TILE = {
  WALL: 0,
  CORRIDOR: 1,
} as const;
export type TileKind = (typeof TILE)[keyof typeof TILE];

export type Tile = { readonly x: number; readonly y: number };

export type FlagSpec = {
  readonly tile: Tile;
  readonly kind: FlagKind;
};

export type Maze = {
  readonly width: number;
  readonly height: number;
  /** Row-major Uint8Array of TileKind. Index = y * width + x. */
  readonly tiles: Uint8Array;
  readonly spawn: Tile;
  readonly flags: readonly FlagSpec[];
  /** Static rocks blocking movement; sit on corridor tiles, never on flag tiles. */
  readonly boulders: readonly Tile[];
};

export type MazeConfig = {
  /** Number of building blocks across. */
  readonly blocksWide: number;
  /** Number of building blocks down. */
  readonly blocksHigh: number;
  /** Width of each building in tiles. */
  readonly blockWidth: number;
  /** Height of each building in tiles. */
  readonly blockHeight: number;
  /** Fraction of buildings that get a 1-tile-wide passage punched through. */
  readonly passageDensity: number;
  readonly flagCount: number;
  readonly minFlagDistance: number;
  readonly boulderCount: number;
};

export const DEFAULT_MAZE_CONFIG: MazeConfig = {
  blocksWide: 8,
  blocksHigh: 7,
  blockWidth: 4,
  blockHeight: 3,
  passageDensity: 0.35,
  flagCount: 13,
  minFlagDistance: 5,
  boulderCount: 6,
};

/** Stable direction order for tie-breaking. */
const DIRS = [
  { dx: 0, dy: -1 },
  { dx: 1, dy: 0 },
  { dx: 0, dy: 1 },
  { dx: -1, dy: 0 },
] as const;

export function mazeWidth(config: MazeConfig): number {
  return config.blocksWide * (config.blockWidth + 1) + 3;
}

export function mazeHeight(config: MazeConfig): number {
  return config.blocksHigh * (config.blockHeight + 1) + 3;
}

export function tileAt(maze: Maze, x: number, y: number): TileKind {
  if (x < 0 || y < 0 || x >= maze.width || y >= maze.height) return TILE.WALL;
  return maze.tiles[y * maze.width + x] as TileKind;
}

export function isWall(maze: Maze, x: number, y: number): boolean {
  return tileAt(maze, x, y) === TILE.WALL;
}

export function isCorridor(maze: Maze, x: number, y: number): boolean {
  return tileAt(maze, x, y) === TILE.CORRIDOR;
}

/** True when a car can move into this tile: corridor AND no boulder. */
export function isPassable(maze: Maze, x: number, y: number): boolean {
  if (tileAt(maze, x, y) !== TILE.CORRIDOR) return false;
  for (const b of maze.boulders) if (b.x === x && b.y === y) return false;
  return true;
}

/** True iff (x, y) holds a boulder. */
export function isBoulder(maze: Maze, x: number, y: number): boolean {
  for (const b of maze.boulders) if (b.x === x && b.y === y) return true;
  return false;
}

/** Indices of all corridor tiles in stable row-major order. */
export function corridorTiles(maze: Maze): Tile[] {
  const out: Tile[] = [];
  for (let y = 0; y < maze.height; y++) {
    for (let x = 0; x < maze.width; x++) {
      if (maze.tiles[y * maze.width + x] === TILE.CORRIDOR) out.push({ x, y });
    }
  }
  return out;
}

/** Flood-fill from `from` through corridor tiles. Pass `excludeBoulders=true` to also skip boulders. */
export function reachableTilesFrom(maze: Maze, from: Tile, excludeBoulders = false): Set<string> {
  const seen = new Set<string>();
  if (!isCorridor(maze, from.x, from.y)) return seen;
  if (excludeBoulders) {
    for (const b of maze.boulders) if (b.x === from.x && b.y === from.y) return seen;
  }
  const queue: Tile[] = [from];
  seen.add(`${from.x},${from.y}`);
  while (queue.length > 0) {
    const t = queue.shift() as Tile;
    for (const d of DIRS) {
      const nx = t.x + d.dx;
      const ny = t.y + d.dy;
      const key = `${nx},${ny}`;
      if (seen.has(key)) continue;
      if (!isCorridor(maze, nx, ny)) continue;
      if (excludeBoulders) {
        let blocked = false;
        for (const b of maze.boulders) {
          if (b.x === nx && b.y === ny) { blocked = true; break; }
        }
        if (blocked) continue;
      }
      seen.add(key);
      queue.push({ x: nx, y: ny });
    }
  }
  return seen;
}

/**
 * Count independent cycles in the corridor graph, assuming a single connected
 * component (the only case the generator produces).
 *   cycles = edges - vertices + 1
 * For a disconnected graph this under-counts by (components - 1); callers
 * should verify connectivity separately (the maze property tests do).
 * A perfect maze has 0 cycles; the hybrid grid produces dozens to hundreds.
 */
export function countCycles(maze: Maze): number {
  let vertices = 0;
  let edges = 0;
  for (let y = 0; y < maze.height; y++) {
    for (let x = 0; x < maze.width; x++) {
      if (maze.tiles[y * maze.width + x] !== TILE.CORRIDOR) continue;
      vertices++;
      // Count edges to E and S only to avoid double-counting.
      if (x + 1 < maze.width && maze.tiles[y * maze.width + x + 1] === TILE.CORRIDOR) edges++;
      if (y + 1 < maze.height && maze.tiles[(y + 1) * maze.width + x] === TILE.CORRIDOR) edges++;
    }
  }
  // Assumes single connected component (callers verify separately).
  return edges - vertices + 1;
}

/** Deterministic 32-bit FNV-1a-style hash of the wall grid. Pure string out. */
export function mazeFingerprint(maze: Maze): string {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < maze.tiles.length; i++) {
    h ^= maze.tiles[i] as number;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return `${maze.width}x${maze.height}:${h.toString(16).padStart(8, '0')}`;
}

/**
 * Generate a maze from a seed. Pure: same seed + config -> identical bytes.
 *
 * Algorithm:
 *  1. Carve every street tile (horizontal and vertical streets on a grid).
 *  2. For each building, with passageDensity probability, carve a single
 *     row or column of it into corridor — adds a shortcut, creates a loop.
 *  3. Spawn at (1, 1), the first street intersection.
 *  4. Greedy flag placement on shuffled corridor list with min-distance.
 *  5. Mark one flag as "special".
 *  6. Place boulders that don't disconnect any flag from spawn.
 */
export function generateMaze(seed: number, config: MazeConfig = DEFAULT_MAZE_CONFIG): Maze {
  const {
    blocksWide,
    blocksHigh,
    blockWidth,
    blockHeight,
    passageDensity,
    flagCount,
    minFlagDistance,
    boulderCount,
  } = config;
  if (blocksWide < 1 || blocksHigh < 1) throw new Error('maze must have at least 1x1 blocks');
  if (blockWidth < 1 || blockHeight < 1) throw new Error('blocks must be at least 1x1');
  // 10 yellow flags + one of each powerup kind. Lower counts would leave the
  // generator unable to place all powerups and could loop forever in the
  // promotion step (`while (taken.has(pickIdx))`).
  if (flagCount < 13) throw new Error('flagCount must be >= 13 (10 yellow + 3 powerups)');

  const width = mazeWidth(config);
  const height = mazeHeight(config);
  const tiles = new Uint8Array(width * height); // all WALL by default

  // (1) Carve a regular street grid. A tile is street when its row or
  // column lands on the street pitch (every blockH+1 / blockW+1 tiles).
  for (let y = 1; y < height - 1; y++) {
    const streetRow = (y - 1) % (blockHeight + 1) === 0;
    for (let x = 1; x < width - 1; x++) {
      const streetCol = (x - 1) % (blockWidth + 1) === 0;
      if (streetRow || streetCol) tiles[y * width + x] = TILE.CORRIDOR;
    }
  }

  let rng: Rng = createRng(seed);

  // (2) Punch passages through some buildings. Adding corridors never
  // disconnects, so no reachability re-check is needed.
  for (let by = 0; by < blocksHigh; by++) {
    for (let bx = 0; bx < blocksWide; bx++) {
      const [roll, r2] = nextFloat(rng);
      rng = r2;
      if (roll >= passageDensity) continue;
      const [orient, r3] = nextFloat(rng);
      rng = r3;
      const bx0 = bx * (blockWidth + 1) + 2;
      const by0 = by * (blockHeight + 1) + 2;
      if (orient < 0.5) {
        const [rowIdx, r4] = nextInt(rng, 0, blockHeight);
        rng = r4;
        const passY = by0 + rowIdx;
        for (let x = bx0; x < bx0 + blockWidth; x++) {
          tiles[passY * width + x] = TILE.CORRIDOR;
        }
      } else {
        const [colIdx, r4] = nextInt(rng, 0, blockWidth);
        rng = r4;
        const passX = bx0 + colIdx;
        for (let y = by0; y < by0 + blockHeight; y++) {
          tiles[y * width + passX] = TILE.CORRIDOR;
        }
      }
    }
  }

  // (3) Spawn at the first intersection.
  const spawn: Tile = { x: 1, y: 1 };

  // (4) Flag placement: shuffle corridor tiles, greedy pick with min distance.
  const baseMaze: Maze = { width, height, tiles, spawn, flags: [], boulders: [] };
  const allCorridors = corridorTiles(baseMaze).filter((t) => !(t.x === spawn.x && t.y === spawn.y));
  const [shuffled, rShuf] = shuffle(rng, allCorridors);
  rng = rShuf;

  const flags: FlagSpec[] = [];
  for (const t of shuffled) {
    if (flags.length === flagCount) break;
    let ok = true;
    for (const f of flags) {
      if (Math.abs(t.x - f.tile.x) + Math.abs(t.y - f.tile.y) < minFlagDistance) {
        ok = false;
        break;
      }
    }
    if (ok) flags.push({ tile: t, kind: 'yellow' });
  }
  if (flags.length < flagCount) {
    for (const t of shuffled) {
      if (flags.length === flagCount) break;
      if (!flags.some((f) => f.tile.x === t.x && f.tile.y === t.y)) {
        flags.push({ tile: t, kind: 'yellow' });
      }
    }
  }
  if (flags.length < flagCount) {
    throw new Error(`could not place ${flagCount} flags in a ${blocksWide}x${blocksHigh}-block maze`);
  }

  // (5) Promote three distinct flags to the three powerup kinds. The
  // deterministic-by-seed RNG means the same seed always picks the same trio.
  const powerupKinds: FlagKind[] = ['doubler', 'fuel_bonus', 'speed_boost'];
  const taken = new Set<number>();
  for (const kind of powerupKinds) {
    const [raw, rPick] = nextInt(rng, 0, flags.length);
    rng = rPick;
    let pickIdx = raw;
    while (taken.has(pickIdx)) pickIdx = (pickIdx + 1) % flags.length;
    taken.add(pickIdx);
    const promoted = flags[pickIdx] as FlagSpec;
    flags[pickIdx] = { tile: promoted.tile, kind };
  }

  // (6) Boulders. Skip spawn/flag tiles; verify every flag is still reachable.
  const baseWithFlags: Maze = { width, height, tiles, spawn, flags, boulders: [] };
  const flagKeys = new Set(flags.map((f) => `${f.tile.x},${f.tile.y}`));
  const boulderCandidates = corridorTiles(baseWithFlags).filter(
    (t) => !(t.x === spawn.x && t.y === spawn.y) && !flagKeys.has(`${t.x},${t.y}`),
  );
  const [shuffledBoulders, rBoul] = shuffle(rng, boulderCandidates);
  rng = rBoul;
  const boulders: Tile[] = [];
  for (const t of shuffledBoulders) {
    if (boulders.length === boulderCount) break;
    const tentativeMaze: Maze = { ...baseWithFlags, boulders: [...boulders, t] };
    const reach = reachableTilesFrom(tentativeMaze, spawn, true);
    const allFlagsReachable = flags.every((f) => reach.has(`${f.tile.x},${f.tile.y}`));
    if (allFlagsReachable) boulders.push(t);
  }

  return { width, height, tiles, spawn, flags, boulders };
}
